import React, { useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Radio,
    TextField,
} from '@mui/material';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import ConfirmDialog from '../../modals/ConfirmDialog';
import { panelTheme } from '../../PanelComerciales.theme';
import { Commercial } from '../../PanelComerciales.types';
import {
    DealMergePayload,
    DuplicateDeal,
    DuplicateGroup,
} from 'src/redux/panel_comerciales/panel_comerciales.types';

interface MergeDealsModalProps {
    group: DuplicateGroup | null;
    commercials: Commercial[];
    busy: boolean;
    onClose: () => void;
    onConfirm: (payload: DealMergePayload) => void | Promise<void>;
}

const EMPTY_VALUE = '—';
/** Valor centinela del selector de owner: conservar el del superviviente. */
const KEEP_OWNER = 0;

const formatDate = (iso: string | null): string =>
    iso ? iso.slice(0, 10) : EMPTY_VALUE;

const formatValue = (value: number | null): string =>
    value === null ? EMPTY_VALUE : `$${value.toLocaleString('es-MX')}`;

/** Filas del comparativo lado a lado: etiqueta + cómo se lee de cada deal. */
const COMPARE_ROWS: { label: string; render: (d: DuplicateDeal) => string }[] = [
    { label: 'Título', render: (d) => d.title ?? EMPTY_VALUE },
    { label: 'Tablero', render: (d) => d.pipeline ?? EMPTY_VALUE },
    { label: 'Etapa', render: (d) => d.stage ?? EMPTY_VALUE },
    {
        label: 'Comercial',
        render: (d) => {
            const activity = d.ownerActive ? '' : ' (inactivo)';
            return `${d.ownerName ?? EMPTY_VALUE}${activity}`;
        },
    },
    { label: 'Equipo', render: (d) => d.ownerTeam ?? EMPTY_VALUE },
    { label: 'Valor', render: (d) => formatValue(d.value) },
    { label: 'Prioridad', render: (d) => d.priority ?? EMPTY_VALUE },
    { label: 'Creado', render: (d) => formatDate(d.createdAt) },
    { label: 'Última actualización', render: (d) => formatDate(d.updateTime) },
    { label: 'Último toque humano', render: (d) => formatDate(d.lastSellerTouch) },
];

/**
 * Modal de resolución de duplicados: comparación lado a lado, elección del
 * deal SUPERVIVIENTE (radio), del deal a fusionar (cuando el grupo tiene más
 * de 2) y del comercial final (opcional). El merge es IRREVERSIBLE: el deal
 * fusionado desaparece de Pipedrive (sus notas y actividades migran al
 * superviviente), por eso confirma con un diálogo destructivo.
 */
const MergeDealsModal: React.FC<MergeDealsModalProps> = ({
    group,
    commercials,
    busy,
    onClose,
    onConfirm,
}) => {
    const [survivingId, setSurvivingId] = useState<number | null>(null);
    const [mergedId, setMergedId] = useState<number | null>(null);
    const [ownerSellerId, setOwnerSellerId] = useState<number>(KEEP_OWNER);
    const [confirming, setConfirming] = useState(false);

    const deals = useMemo(() => group?.deals ?? [], [group]);
    const activeCommercials = useMemo(
        () => commercials.filter((c) => c.active),
        [commercials],
    );

    const effectiveMergedId = useMemo(() => {
        if (deals.length === 2 && survivingId !== null) {
            return deals.find((d) => d.dealId !== survivingId)?.dealId ?? null;
        }
        return mergedId;
    }, [deals, survivingId, mergedId]);

    const ready =
        survivingId !== null &&
        effectiveMergedId !== null &&
        effectiveMergedId !== survivingId;

    const reset = () => {
        setSurvivingId(null);
        setMergedId(null);
        setOwnerSellerId(KEEP_OWNER);
        setConfirming(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleConfirm = async () => {
        if (!ready || survivingId === null || effectiveMergedId === null) return;
        const payload: DealMergePayload = {
            surviving_deal_id: survivingId,
            merged_deal_id: effectiveMergedId,
        };
        if (ownerSellerId !== KEEP_OWNER) {
            payload.new_owner_seller_id = ownerSellerId;
        }
        setConfirming(false);
        await onConfirm(payload);
        reset();
    };

    return (
        <>
            <Dialog open={Boolean(group)} onClose={handleClose} maxWidth='md' fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CallMergeIcon sx={{ color: panelTheme.colors.indigo }} />
                    Resolver deals duplicados
                </DialogTitle>
                <DialogContent>
                    <Alert severity='warning' sx={{ mb: 2 }}>
                        El merge es irreversible: el deal fusionado desaparece de
                        Pipedrive y sus notas y actividades pasan al superviviente.
                    </Alert>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: 6 }}>Campo</th>
                                    {deals.map((d) => (
                                        <th key={d.dealId} style={{ textAlign: 'left', padding: 6 }}>
                                            Deal {d.dealId}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{ background: '#F9FAFB' }}>
                                    <td style={{ padding: 6 }}><strong>Sobrevive</strong></td>
                                    {deals.map((d) => (
                                        <td key={d.dealId} style={{ padding: 6 }}>
                                            <Radio
                                                size='small'
                                                checked={survivingId === d.dealId}
                                                onChange={() => setSurvivingId(d.dealId)}
                                                inputProps={{
                                                    'aria-label': `Sobrevive el deal ${d.dealId}`,
                                                }}
                                            />
                                        </td>
                                    ))}
                                </tr>
                                {deals.length > 2 && (
                                    <tr>
                                        <td style={{ padding: 6 }}><strong>Se fusiona</strong></td>
                                        {deals.map((d) => (
                                            <td key={d.dealId} style={{ padding: 6 }}>
                                                <Radio
                                                    size='small'
                                                    disabled={survivingId === d.dealId}
                                                    checked={mergedId === d.dealId}
                                                    onChange={() => setMergedId(d.dealId)}
                                                    inputProps={{
                                                        'aria-label': `Se fusiona el deal ${d.dealId}`,
                                                    }}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                )}
                                {COMPARE_ROWS.map((row) => (
                                    <tr key={row.label}>
                                        <td style={{ padding: 6, color: '#6B7280' }}>{row.label}</td>
                                        {deals.map((d) => (
                                            <td key={d.dealId} style={{ padding: 6 }}>
                                                {row.render(d)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <TextField
                        select
                        fullWidth
                        size='small'
                        sx={{ mt: 2 }}
                        label='Comercial final del deal superviviente'
                        value={ownerSellerId}
                        onChange={(e) => setOwnerSellerId(Number(e.target.value))}
                        helperText='Conserva el del superviviente, o elige a uno de los dos o a un tercero activo.'
                    >
                        <MenuItem value={KEEP_OWNER}>
                            Conservar el comercial del superviviente
                        </MenuItem>
                        {activeCommercials.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name} · {c.team}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant='contained'
                        color='error'
                        disabled={!ready || busy}
                        onClick={() => setConfirming(true)}
                        sx={{ textTransform: 'none' }}
                    >
                        Fusionar deals
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirming}
                danger
                title='¿Fusionar los deals?'
                description={
                    `El deal ${effectiveMergedId ?? ''} se fusionará dentro del ` +
                    `deal ${survivingId ?? ''} y desaparecerá de Pipedrive. ` +
                    'Esta acción no se puede deshacer.'
                }
                confirmLabel='Sí, fusionar'
                cancelLabel='Cancelar'
                onConfirm={handleConfirm}
                onCancel={() => setConfirming(false)}
            />
        </>
    );
};

export default MergeDealsModal;
